/**
 * Hooks del Catálogo inteligente (Fase 1) — categorías, atributos dinámicos,
 * relaciones producto↔producto (compatibilidad bidireccional) y fichas/adjuntos.
 *
 * Backend: apps.sales (PR1 #448, PR2 #451, PR3 #453). Todo reusa el permiso
 * `sales.products` — no hay módulo RBAC nuevo.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

// ─── Tipos ──────────────────────────────────────────────────────────────────

export interface ProductCategory {
  id: string
  name: string
  parent: string | null
  parent_name: string | null
  description: string
  /** Centro heredado por los productos de la categoría que no definan uno propio. */
  default_cost_center: string | null
  is_reference: boolean
  is_active: boolean
  product_count: number
  created_at: string
  updated_at: string
}

export type AttributeDataType = 'text' | 'number' | 'select' | 'boolean'

export interface ProductAttribute {
  id: string
  category: string
  category_name: string
  name: string
  code: string
  data_type: AttributeDataType
  data_type_display: string
  options: string[]
  is_filterable: boolean
  is_required: boolean
  order: number
  is_active: boolean
  created_at: string
  updated_at: string
}

export type RelationshipType =
  | 'compatible_with'
  | 'equivalent_to'
  | 'substitute'
  | 'accessory_of'
  | 'component_of'

/** Etiquetas y dirección de cada tipo de relación (espejo del backend). */
export const RELATIONSHIP_TYPES: { value: RelationshipType; label: string; symmetric: boolean }[] = [
  { value: 'compatible_with', label: 'Compatible con', symmetric: true },
  { value: 'equivalent_to', label: 'Equivalente a', symmetric: true },
  { value: 'substitute', label: 'Sustituto de', symmetric: true },
  { value: 'accessory_of', label: 'Accesorio de', symmetric: false },
  { value: 'component_of', label: 'Componente de', symmetric: false },
]

export interface ProductRelationship {
  id: string
  from_product: string
  to_product: string
  relationship_type: RelationshipType
  relationship_type_display: string
  notes: string
  from_product_name: string
  from_product_sku: string
  to_product_name: string
  to_product_sku: string
  created_at: string
}

export interface RelatedProduct {
  product_id: string
  sku: string
  name: string
  category_name: string | null
  track_stock: boolean
  is_active: boolean
  relationship_type: RelationshipType
  relationship_type_display: string
  direction: 'outgoing' | 'incoming'
}

export interface ProductRelatedResponse {
  product_id: string
  related: RelatedProduct[]
  count: number
}

export interface ProductAttachment {
  id: string
  product: string
  file_name: string
  file_size: number
  content_type: string
  title: string
  download_url: string
  uploaded_by: string | null
  uploaded_by_name: string | null
  created_at: string
}

export interface ProductAttachmentListResponse {
  count: number
  total_size: number
  max_size: number
  attachments: ProductAttachment[]
}

// ─── Query keys ───────────────────────────────────────────────────────────────

export const catalogKeys = {
  categories: (params?: object) => ['sales', 'catalog', 'categories', params] as const,
  attributes: (params?: object) => ['sales', 'catalog', 'attributes', params] as const,
  relationships: (params?: object) => ['sales', 'catalog', 'relationships', params] as const,
  related: (productId: string, params?: object) => ['sales', 'catalog', 'related', productId, params] as const,
  attachments: (productId: string) => ['sales', 'catalog', 'attachments', productId] as const,
}

// ─── Categorías ─────────────────────────────────────────────────────────────

export function useProductCategories(params?: {
  active?: string
  is_reference?: string
  parent?: string
}) {
  return useQuery<{ categories: ProductCategory[] }>({
    queryKey: catalogKeys.categories(params),
    queryFn: async () => {
      const res = await apiClient.get('/sales/product-categories/', { params })
      return res.data
    },
  })
}

export function useCreateProductCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<ProductCategory>) => {
      const res = await apiClient.post('/sales/product-categories/', data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales', 'catalog', 'categories'] }),
  })
}

export function useUpdateProductCategory(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<ProductCategory>) => {
      const res = await apiClient.patch(`/sales/product-categories/${id}/`, data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales', 'catalog', 'categories'] }),
  })
}

export function useDeleteProductCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/sales/product-categories/${id}/`)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales', 'catalog', 'categories'] }),
  })
}

// ─── Atributos ──────────────────────────────────────────────────────────────

export function useProductAttributes(params?: {
  category?: string
  filterable?: string
  active?: string
}) {
  return useQuery<{ attributes: ProductAttribute[] }>({
    queryKey: catalogKeys.attributes(params),
    queryFn: async () => {
      const res = await apiClient.get('/sales/product-attributes/', { params })
      return res.data
    },
    // category vacío = no filtrar; igual trae todos. Permitido siempre.
  })
}

export function useCreateProductAttribute() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<ProductAttribute>) => {
      const res = await apiClient.post('/sales/product-attributes/', data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales', 'catalog', 'attributes'] }),
  })
}

export function useUpdateProductAttribute(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<ProductAttribute>) => {
      const res = await apiClient.patch(`/sales/product-attributes/${id}/`, data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales', 'catalog', 'attributes'] }),
  })
}

export function useDeleteProductAttribute() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/sales/product-attributes/${id}/`)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales', 'catalog', 'attributes'] }),
  })
}

// ─── Relaciones (compatibilidad) ──────────────────────────────────────────────

export function useProductRelationships(params?: {
  product?: string
  relationship_type?: string
  search?: string
}) {
  return useQuery<{ relationships: ProductRelationship[] }>({
    queryKey: catalogKeys.relationships(params),
    queryFn: async () => {
      const res = await apiClient.get('/sales/product-relationships/', { params })
      return res.data
    },
  })
}

export function useCreateProductRelationship() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      from_product: string
      to_product: string
      relationship_type: RelationshipType
      notes?: string
    }) => {
      const res = await apiClient.post('/sales/product-relationships/', data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'catalog', 'relationships'] })
      qc.invalidateQueries({ queryKey: ['sales', 'catalog', 'related'] })
    },
  })
}

export function useUpdateProductRelationship() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...data }: {
      id: string
      relationship_type?: RelationshipType
      notes?: string
    }) => {
      const res = await apiClient.patch(`/sales/product-relationships/${id}/`, data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'catalog', 'relationships'] })
      qc.invalidateQueries({ queryKey: ['sales', 'catalog', 'related'] })
    },
  })
}

export function useDeleteProductRelationship() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/sales/product-relationships/${id}/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'catalog', 'relationships'] })
      qc.invalidateQueries({ queryKey: ['sales', 'catalog', 'related'] })
    },
  })
}

/** Consulta bidireccional: dado un producto, los productos relacionados (ambas direcciones). */
export function useProductRelated(productId: string, params?: { relationship_type?: string }) {
  return useQuery<ProductRelatedResponse>({
    queryKey: catalogKeys.related(productId, params),
    queryFn: async () => {
      const res = await apiClient.get(`/sales/products/${productId}/related/`, { params })
      return res.data
    },
    enabled: !!productId,
  })
}

// ─── Fichas / adjuntos ────────────────────────────────────────────────────────

export function useProductAttachments(productId: string) {
  return useQuery<ProductAttachmentListResponse>({
    queryKey: catalogKeys.attachments(productId),
    queryFn: async () => {
      const res = await apiClient.get(`/sales/products/${productId}/attachments/`)
      return res.data
    },
    enabled: !!productId,
  })
}

export function useUploadProductAttachment(productId: string) {
  const qc = useQueryClient()
  return useMutation<ProductAttachment, Error, { file: File; title?: string }>({
    mutationFn: async ({ file, title }) => {
      const form = new FormData()
      form.append('file', file)
      if (title) form.append('title', title)
      const res = await apiClient.post(`/sales/products/${productId}/attachments/`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: catalogKeys.attachments(productId) }),
  })
}

export function useDeleteProductAttachment(productId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (attachmentId: string) => {
      const res = await apiClient.delete(`/sales/products/${productId}/attachments/${attachmentId}/`)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: catalogKeys.attachments(productId) }),
  })
}
